import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlanificacion } from '@context/PlanificacionContext';
import { Target, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import Button from '@components/common/Button';

// Los 7 enfoques estándar del CNEB (Perú)
const ENFOQUES_TRANSVERSALES = [
  { id: 1, nombre: 'Enfoque de Derechos', color: 'bg-red-100 text-red-800 border-red-200' },
  { id: 2, nombre: 'Enfoque Inclusivo o Atención a la Diversidad', color: 'bg-orange-100 text-orange-800 border-orange-200' },
  { id: 3, nombre: 'Enfoque Intercultural', color: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { id: 4, nombre: 'Enfoque Igualdad de Género', color: 'bg-pink-100 text-pink-800 border-pink-200' },
  { id: 5, nombre: 'Enfoque Ambiental', color: 'bg-green-100 text-green-800 border-green-200' },
  { id: 6, nombre: 'Enfoque Orientación al Bien Común', color: 'bg-blue-100 text-blue-800 border-blue-200' },
  { id: 7, nombre: 'Enfoque Búsqueda de la Excelencia', color: 'bg-purple-100 text-purple-800 border-purple-200' }
];

const EnfoquesPage = () => {
  const navigate = useNavigate();
  const { unidades, enfoquesUnidades, toggleEnfoqueUnidad } = usePlanificacion();

  // Redirigir si no hay unidades creadas
  useEffect(() => {
    if (unidades.length === 0) {
      navigate('/unidades');
    }
  }, [unidades, navigate]);

  // Asegurar que las unidades estén ordenadas cronológicamente
  const unidadesOrdenadas = [...unidades].sort((a, b) => a.id - b.id);

  if (unidades.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 pb-4 border-b">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Target className="w-6 h-6 text-blue-600" /> Matriz de Enfoques Transversales
          </h1>
          <p className="text-gray-500 mt-1">Selecciona qué enfoques se trabajarán en cada unidad o proyecto.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/unidades')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Volver
          </Button>
          <Button onClick={() => navigate('/generar-documentos')} className="bg-blue-600 text-white flex items-center gap-2">
            Ir a Generar Word <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Contenedor de la Tabla con scroll horizontal en pantallas pequeñas */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-4 font-bold text-gray-700 w-1/4 sticky left-0 bg-gray-50 z-10 shadow-[2px_0_5px_rgba(0,0,0,0.02)]">
                  Enfoques / Unidades
                </th>
                {unidadesOrdenadas.map((unidad, idx) => (
                  <th key={unidad.id} className="px-4 py-4 text-center min-w-[120px] border-l border-gray-100">
                    <div className="text-xs text-blue-600 font-bold uppercase mb-1">Unidad {idx + 1}</div>
                    <div className="text-gray-800 font-semibold truncate w-32 mx-auto" title={unidad.titulo || 'Sin título'}>
                      {unidad.titulo || `Unidad ${idx + 1}`}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {ENFOQUES_TRANSVERSALES.map((enfoque) => {
                // Calcular cuántas unidades tienen este enfoque seleccionado
                const vecesSeleccionado = unidadesOrdenadas.reduce((acc, u) => {
                  return acc + ((enfoquesUnidades[u.id] || []).includes(enfoque.id) ? 1 : 0);
                }, 0);

                return (
                  <tr key={enfoque.id} className="hover:bg-gray-50 transition-colors">
                    {/* Celda del nombre del enfoque (Fija a la izquierda) */}
                    <td className="px-6 py-4 sticky left-0 bg-white group-hover:bg-gray-50 z-10 shadow-[2px_0_5px_rgba(0,0,0,0.02)] border-r border-gray-100">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${enfoque.color}`}>
                        {enfoque.nombre}
                      </div>
                      {vecesSeleccionado > 0 && (
                        <span className="ml-2 text-[10px] font-bold text-gray-400">
                          (En {vecesSeleccionado} unids)
                        </span>
                      )}
                    </td>

                    {/* Checkboxes por cada Unidad */}
                    {unidadesOrdenadas.map((unidad) => {
                      const estaSeleccionado = (enfoquesUnidades[unidad.id] || []).includes(enfoque.id);
                      
                      return (
                        <td 
                          key={unidad.id} 
                          className="px-4 py-4 text-center border-l border-gray-100 cursor-pointer group/cell"
                          onClick={() => toggleEnfoqueUnidad(unidad.id, enfoque.id)}
                        >
                          <div className={`
                            mx-auto w-6 h-6 rounded flex items-center justify-center transition-all duration-200 border-2
                            ${estaSeleccionado 
                              ? 'bg-blue-600 border-blue-600 shadow-md scale-110' 
                              : 'bg-white border-gray-300 group-hover/cell:border-blue-400 group-hover/cell:bg-blue-50'
                            }
                          `}>
                            {estaSeleccionado && <Check className="w-4 h-4 text-white stroke-[3]" />}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Footer Informativo */}
        <div className="bg-blue-50 p-4 border-t text-sm text-blue-800 flex items-center gap-2">
          <Target className="w-5 h-5" />
          <p>
            <strong>Tip pedagógico:</strong> No es necesario abarcar todos los enfoques en todas las unidades. 
            Se recomienda seleccionar <strong>1 o 2 enfoques principales</strong> por unidad según tu situación significativa.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EnfoquesPage;